import axios from 'axios'

const API_BASE_URL = 'https://empresa-service.intrare.app'
const API_ENDPOINT = '/transports/'

class TransportService {
  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000
    })
  }

  getAuthHeaders() {
    const token = localStorage.getItem('user-token')
    return {
      'Authorization': `Token ${token}`,
      'Content-Type': 'application/json'
    }
  }

  // 1. Obtener lista de transportes
  async getTransports(params = {}) {
    try {
      const response = await this.axiosInstance.get(API_ENDPOINT, {
        headers: this.getAuthHeaders(),
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          search: params.search || undefined
        }
      })
      return response.data
    } catch (error) {
      console.error('Error obteniendo transportes:', error)
      throw this.handleError(error)
    }
  }

  // 2. Obtener detalle de un transporte
  async getTransportById(id) {
    try {
      const response = await this.axiosInstance.get(`${API_ENDPOINT}${id}/`, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error obteniendo transporte:', error)
      throw this.handleError(error)
    }
  }

  // 3. Crear un nuevo transporte
  async createTransport(data) {
    try {
      const payload = {
        name: data.name,
        plate: data.plate,
        capacity: data.capacity,
        driver_first_name: data.driver_name,
        driver_last_name: data.driver_last_name
      }

      const response = await this.axiosInstance.post(API_ENDPOINT, payload, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error creando transporte:', error)
      throw this.handleError(error)
    }
  }

  // 4. Actualizar un transporte
  async updateTransport(id, data) {
    try {
      const payload = {
        name: data.name,
        plate: data.plate,
        capacity: data.capacity,
        status: data.status || 'active'
      }

      const response = await this.axiosInstance.patch(`${API_ENDPOINT}${id}/`, payload, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error actualizando transporte:', error)
      throw this.handleError(error)
    }
  }

  // 5. Actualizar solo datos del conductor
  async updateDriver(id, driverData) {
    try {
      const payload = {
        first_name: driverData.driver_name,
        last_name: driverData.driver_last_name
      }

      const response = await this.axiosInstance.patch(`${API_ENDPOINT}${id}/driver/`, payload, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error actualizando conductor:', error)
      throw this.handleError(error)
    }
  }

  // 6. Eliminar un transporte
  async deleteTransport(id) {
    try {
      const response = await this.axiosInstance.delete(`${API_ENDPOINT}${id}/`, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error eliminando transporte:', error)
      throw this.handleError(error)
    }
  }

  // 7. Obtener historial de accesos de un transporte
  async getTransportAccesses(id, params = {}) {
    try {
      const response = await this.axiosInstance.get(`${API_ENDPOINT}${id}/accesses/`, {
        headers: this.getAuthHeaders(),
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          type: params.type || undefined,
          date_from: params.date_from || undefined,
          date_to: params.date_to || undefined
        }
      })
      return response.data
    } catch (error) {
      console.error('Error obteniendo accesos del transporte:', error)
      throw this.handleError(error)
    }
  }

  // 8. Registrar acceso de transporte (entrada/salida)
  async registerAccess(qrCode, driverName, accessType, parkId = null, location = null) {
    try {
      const payload = {
        qr_code: qrCode,
        driver_name: driverName,
        access_type: accessType,
        park_id: parkId,
        location: location
      }

      const response = await this.axiosInstance.post(`${API_ENDPOINT}access/register/`, payload, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error registrando acceso:', error)
      throw this.handleError(error)
    }
  }

  // 9. Obtener transporte por código QR
  async getTransportByQR(qrCode) {
    try {
      const response = await this.axiosInstance.get(`${API_ENDPOINT}qr/${qrCode}/`, {
        headers: this.getAuthHeaders()
      })
      return response.data
    } catch (error) {
      console.error('Error obteniendo transporte por QR:', error)
      throw this.handleError(error)
    }
  }

  // Mapear datos del backend al formato esperado por el frontend
  mapTransportToFrontend(backendTransport) {
    const driver = backendTransport.driver || {}
    return {
      id: backendTransport.id,
      name: backendTransport.name,
      plate: backendTransport.plate,
      driver_name: driver.first_name || '',
      driver_last_name: driver.last_name || '',
      capacity: backendTransport.capacity,
      status: backendTransport.status,
      qr_code: backendTransport.qr_code,
      created_at: backendTransport.created_at,
      accesses: this.mapAccesses(backendTransport.accesses)
    }
  }

  // Mapear accesos del backend al formato esperado por el frontend
  mapAccesses(backendAccesses = []) {
    return backendAccesses.map(access => ({
      id: access.id,
      date: new Date(access.timestamp).toLocaleString('es-MX'),
      location: access.location || access.park_name,
      type: access.access_type === 'entrada' ? 'entrada' : 'salida'
    }))
  }

  // Manejo de errores
  handleError(error) {
    if (error.response) {
      const status = error.response.status
      const data = error.response.data
      const errors = data.errors || {}

      if (status === 400) {
        const plateError = errors.plate && errors.plate[0]
        return new Error(plateError || 'Validación fallida')
      } else if (status === 401) {
        return new Error('No autenticado. Por favor, inicia sesión.')
      } else if (status === 404) {
        return new Error('Transporte no encontrado')
      } else if (status === 409) {
        return new Error('La placa ya está registrada')
      } else {
        return new Error(data.message || 'Error del servidor')
      }
    } else if (error.request) {
      return new Error('No hay conexión con el servidor')
    } else {
      return new Error(error.message)
    }
  }
}

export default new TransportService()
